import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  IconButton
} from '@mui/material';
import {
  Close
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

// Custom RangeDay component for highlighting date ranges
function RangeDay(props) {
  const { day, outsideCurrentMonth, startDate, endDate, ...other } = props;

  const isStart = startDate && dayjs(day).isSame(dayjs(startDate), 'day');
  const isEnd = endDate && dayjs(day).isSame(dayjs(endDate), 'day');

  const inRange = startDate && endDate && dayjs(day).isAfter(dayjs(startDate), 'day') && dayjs(day).isBefore(dayjs(endDate), 'day') && !isStart && !isEnd;


  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      sx={{
        bgcolor: isStart || isEnd ? "#627CFB" : inRange ? "rgba(98, 124, 251, 0.1)" : undefined,
        color: isStart || isEnd ? "white" : undefined,
        borderRadius: isStart || isEnd ? "50%" : 0,
        "&:hover": {
          bgcolor: isStart || isEnd ? "#627CFB" : inRange ? "rgba(98, 124, 251, 0.2)" : "rgba(0, 0, 0, 0.04)",
          color: isStart || isEnd ? "white" : undefined,
        },
      }}
    />
  );
}

const CustomDateRangeDialog = ({ open, onClose, onApply, initialStartDate = '', initialEndDate = '' }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (initialStartDate && initialEndDate) {
      setStartDate(dayjs(initialStartDate));
      setEndDate(dayjs(initialEndDate));
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [initialStartDate, initialEndDate]);


  const handleApply = () => {
    if (startDate && endDate) {
      // Ensure start date is not after end date
      let finalStartDate = startDate;
      let finalEndDate = endDate;
      
      if (dayjs(startDate).isAfter(dayjs(endDate), 'day')) {
        // Swap if start is after end
        finalStartDate = endDate;
        finalEndDate = startDate;
      }
      
      const startDateStr = dayjs(finalStartDate).format('YYYY-MM-DD');
      const endDateStr = dayjs(finalEndDate).format('YYYY-MM-DD');
      onApply(startDateStr, endDateStr);
    }
  };

  const handleCancel = () => {
    setStartDate(null);
    setEndDate(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 999999
      }}
      PaperProps={{
        sx: {
          minHeight: '600px',
          zIndex: 999999
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Date Range
        </Typography>
        <IconButton onClick={handleCancel} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* Date Range Display with Month Names */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {startDate 
                ? `${dayjs(startDate).format('dddd')}, ${dayjs(startDate).format('YYYY-MM-DD')}`
                : 'Select start date'
              }
            </Typography>
            
            {startDate && endDate && (
              <Box sx={{ color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                </svg>
              </Box>
            )}
            
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {endDate 
                ? `${dayjs(endDate).format('dddd')}, ${dayjs(endDate).format('YYYY-MM-DD')}`
                : startDate 
                  ? 'Select end date'
                  : ''
              }
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Two Date Calendars with Range Highlighting */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Start Date Calendar */}
            <Box sx={{ flex: 1 }}>
              <DateCalendar
                value={null}
                onChange={(date) => {
                  if (!startDate || (startDate && endDate)) {
                    setStartDate(date);
                    setEndDate(null);
                  } else {
                    if (dayjs(date).isAfter(dayjs(startDate), 'day') || dayjs(date).isSame(dayjs(startDate), 'day')) {
                      setEndDate(date);
                    } else {
                      setEndDate(startDate);
                      setStartDate(date);
                    }
                  }
                }}
                slots={{ day: RangeDay }}
                slotProps={{ day: { startDate, endDate } }}
                sx={{
                  width: '100%',
                  '& .MuiPickersCalendarHeader-root': {
                    paddingLeft: 0,
                    paddingRight: 0,
                  }
                }}
              />
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* End Date Calendar */}
            <Box sx={{ flex: 1 }}>
              <DateCalendar
                value={null}
                onChange={(date) => {
                  if (!startDate || (startDate && endDate)) {
                    setStartDate(date);
                    setEndDate(null);
                  } else {
                    if (dayjs(date).isAfter(dayjs(startDate), 'day') || dayjs(date).isSame(dayjs(startDate), 'day')) {
                      setEndDate(date);
                    } else {
                      setEndDate(startDate);
                      setStartDate(date);
                    }
                  }
                }}
                slots={{ day: RangeDay }}
                slotProps={{ day: { startDate, endDate } }}
                sx={{
                  width: '100%',
                  '& .MuiPickersCalendarHeader-root': {
                    paddingLeft: 0,
                    paddingRight: 0,
                  }
                }}
              />
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleCancel}
          sx={{ 
            color: '#627CFB', 
            textTransform: 'none', 
            fontWeight: 500 
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleApply}
          variant="contained"
          disabled={!startDate || !endDate}
          sx={{ 
            backgroundColor: '#627CFB', 
            textTransform: 'none', 
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#4f46e5'
            }
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomDateRangeDialog;